import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';

import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomChip from '@ui-components/Chip/CustomChip';

import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { rowsPerPage } from '../constants';

import { useLocation, useNavigate } from 'react-router-dom';
import CustomPagination from '@ui-components/Table/CustomPagination';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import useDebouncing from '../components/useDebouncing';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import { useGetOpsStaffList } from '../../API/hooks/opsStaffHook';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import {
  NEW_OPSSTAFF_ROUTE_PATH,
  OPSSTAFF_ROUTE_PATH,
  VIEW_OPSSTAFF_ROUTE_PATH,
} from '../../routers/routePaths';
import FetchEntraUsers from '../components/FetchEntraUsers';
import { useGetAzureEntraUsers } from '../../API/hooks/ClinicianHook';

import EditOnlyIcon from '@assets/images/editOnly.svg';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';
import CustomPopover from '@ui-components/Popover/CustomPopover';
import { StatusFilterSelector } from '../components/StatusFilterSelector';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import { RoleFilterSelector } from '../components/RoleFilterSelector';
import CustomLink from '@ui-components/Link/CustomLink';
import { hasRole } from '../login/storageUtility';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import { useFilters } from '../../routers/FilterContext';
import { CustomBadge } from '@qler-view/shared_lib/src';

const OpsStaffList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');

  const { filterContext, setFilterContext, getFilterCounts } = useFilters();

  const [selectedStatusId, setSelectedStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [filterStatusId, setFilterStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>(
    filterContext.roleIds
  );
  const [filterRoleIds, setFilterRoleIds] = useState<number[]>(
    filterContext.roleIds
  );
  const [anchorOpsStaffListEl, setanchorOpsStaffListEl] =
    useState<HTMLElement | null>(null);
  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );

  const [searchTermOpsStaff, setSearchTermOpsStaff] = useState<string>(
    filterContext.searchedValue
  );
  const debouncedSearchTerm = useDebouncing(searchTermOpsStaff, 500);

  const {
    azureEntraUsers,
    refetch: refetchAzureEntraUsers,
    isLoading: isLoadingAzureEntraUsers,
  } = useGetAzureEntraUsers('USER');

  const [openAzureEntraUsersForOpsStaff, setOpenAzureEntraUsersForOpsStaff] =
    useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStatusId, selectedRoleIds]);

  useEffect(() => {
    setSelectedRoleIds(filterContext.roleIds);
    setSelectedStatusId(filterContext.statusIds);
    setSearchTermOpsStaff(filterContext.searchedValue);
  }, [filterContext]);

  const { OpsStaffListResponse, isLoading } = useGetOpsStaffList({
    userId: '1',
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    statusId: selectedStatusId,
    roleId: selectedRoleIds,
  });

  const OpsStaffListCount = OpsStaffListResponse?.totalRecords ?? 0;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus the input field
    }
  }, [searchTermOpsStaff]);

  const {
    PartnerFacilityFilterData,
    // isLoading: isLoadingFilters,
    refetch: refetchFilters,
  } = useGetPartnerFacilityFilterData();

  const handleOpsStaffListFilterClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setanchorOpsStaffListEl(event.currentTarget);
    refetchFilters();
  };
  const handleOpenAzureEntraForOpsStaffForm = (open: boolean) => {
    setOpenAzureEntraUsersForOpsStaff(open);
    refetchAzureEntraUsers();
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handleOpsStaffSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove leading spaces
    value = value.replace(/^\s+/, '');

    // Replace multiple spaces with a single space
    value = value.replaceAll(/\s{2,}/g, ' ');

    setSearchTermOpsStaff(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleViewOpsStaffInfo = (opsStaffId: number) => {
    navigate(VIEW_OPSSTAFF_ROUTE_PATH + `/${opsStaffId}`, { replace: true });
  };

  const handleEditOpsStaffInfo = (opsStaffId: number) => {
    navigate(NEW_OPSSTAFF_ROUTE_PATH + `/${opsStaffId}`, {
      replace: true,
      state: { isOpsList: true },
    });
  };
  const handleApplyFilters = () => {
    setSelectedStatusId(filterStatusId);
    setSelectedRoleIds(filterRoleIds);
    setFilterContext({
      ...filterContext,
      statusIds: filterStatusId, // update only this
      roleIds: filterRoleIds,
    });
  };

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Ops user saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Ops user updated successfully!');
      setOpenToast(true);
    }

    navigate(OPSSTAFF_ROUTE_PATH, { replace: true });
  }, [toast, navigate]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="ops-staff-snackbar"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="ops-staff-title"
              variant="h4"
              className="text-align-left"
            >
              Ops Users
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-search-ops-staff"
                  name="search-ops-staff"
                  placeholder="Search Ops User"
                  size="small"
                  type="text"
                  value={searchTermOpsStaff}
                  onChange={handleOpsStaffSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleOpsStaffListFilterClick(e);
                    }}
                    className="box-border2"
                    id="status-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-opsstaff"
                    color="primary"
                    size="large"
                    onClick={() => {
                      handleOpenAzureEntraForOpsStaffForm(true);
                    }}
                  >
                    Add Ops User
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CustomTypography
              id="loading-text"
              variant="h5"
              className="text-align-center pt-32"
              color="textDisabled"
            >
              Loading...
            </CustomTypography>
          </Box>
        ) : (
          <CustomTable id="table-ops-staff-list">
            <CustomTableHead id="table-head-ops-staff-list">
              <CustomTableRow id="ops-staff-header">
                <CustomTableCell
                  id="table-cell-ops-staff-user-id"
                  onClick={() => handleSort('opsStaffBusinessId')}
                >
                  USER ID&nbsp;
                  {sortField === 'opsStaffBusinessId' && getSortIcon(sortType)}
                </CustomTableCell>

                <CustomTableCell
                  id="table-cell-ops-staff-first-name"
                  onClick={() => handleSort('firstName')}
                >
                  FIRST NAME&nbsp;
                  {sortField === 'firstName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-ops-staff-last-name"
                  onClick={() => handleSort('lastName')}
                >
                  LAST NAME&nbsp;
                  {sortField === 'lastName' && getSortIcon(sortType)}
                </CustomTableCell>

                <CustomTableCell
                  id="table-cell-ops-staff-primary-email"
                  onClick={() => handleSort('email')}
                >
                  PRIMARY EMAIL &nbsp;
                  {sortField === 'email' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-user-role"
                  onClick={() => handleSort('role')}
                >
                  ROLE &nbsp;
                  {sortField === 'role' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-ops-staff-status"
                  onClick={() => handleSort('status')}
                >
                  STATUS &nbsp;
                  {sortField === 'status' && getSortIcon(sortType)}
                </CustomTableCell>
                {hasRole(['ops-admin']) && (
                  <CustomTableCell
                    id="table-cell-ops-staff-status"
                    onClick={() => handleSort('status')}
                  >
                    ACTIONS &nbsp;
                  </CustomTableCell>
                )}
              </CustomTableRow>
            </CustomTableHead>
            {OpsStaffListResponse &&
            OpsStaffListResponse?.opsStaffList.length > 0 ? (
              <CustomTableBody id="table-body-ops-staff-list">
                {OpsStaffListResponse?.opsStaffList.map((opsStaff) => (
                  <CustomTableRow
                    key={opsStaff.id}
                    id={`table-row-ops-staff-${opsStaff.id}`}
                  >
                    <CustomTableCell
                      id={`table-cell-ops-staff-id-${opsStaff.id}`}
                    >
                      <CustomLink
                        id={`link-ops-staff-id-${opsStaff.id}`}
                        color="primary"
                        onClick={() => handleViewOpsStaffInfo(opsStaff.id)}
                      >
                        {opsStaff.opsStaffBusinessId}
                      </CustomLink>
                    </CustomTableCell>

                    <CustomTableCell
                      id={`table-cell-ops-staff-first-name-${opsStaff.id}`}
                    >
                      {opsStaff.firstName}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-ops-staff-last-name-${opsStaff.id}`}
                    >
                      {opsStaff.lastName}
                    </CustomTableCell>

                    <CustomTableCell
                      id={`table-cell-ops-staff-primary-email-${opsStaff.id}`}
                    >
                      {opsStaff.email}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-ops-staff-role-${opsStaff.id}`}
                    >
                      {opsStaff.role}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-ops-staff-status-${opsStaff.id}`}
                    >
                      {opsStaff.status}
                    </CustomTableCell>
                    {hasRole(['ops-admin']) && (
                      <CustomTableCell
                        id={`table-cell-ops-staff-action-${opsStaff.id}`}
                        className="width-10"
                      >
                        <Box className="flex align-items-center">
                          <Box className="ml-16">
                            <CustomIconButton
                              id="edit-icon"
                              size="small"
                              icon={<EditOnlyIcon />}
                              onClick={() => {
                                handleEditOpsStaffInfo(opsStaff.id);
                              }}
                            />
                          </Box>
                        </Box>
                      </CustomTableCell>
                    )}
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            ) : (
              <CustomTableBody id="table-body-ops-staff-list">
                <CustomTableRow id="table-row-ops-staff">
                  <CustomTableCell id="table-cell-ops-staff-id" colspan={7}>
                    <Box className="flex flex-justify-center align-items-center">
                      <CustomTypography
                        id="no-ops-staff-found"
                        variant="body2"
                        color="textDisabled"
                      >
                        No Ops User Found
                      </CustomTypography>
                    </Box>
                  </CustomTableCell>
                </CustomTableRow>
              </CustomTableBody>
            )}
          </CustomTable>
        )}
        {OpsStaffListResponse && OpsStaffListCount > 0 && (
          <Box className="mt-32">
            <Grid2 container className="align-items-center">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <Box className="flex justify-content-start">
                  <CustomTypography
                    id="showing-row-count-text"
                    variant="body2"
                    color="textDisabled"
                  >
                    Showing {getStartItem(page, rowsPerPage)} -{' '}
                    {getEndItem(page, rowsPerPage, OpsStaffListCount)} of{' '}
                    {OpsStaffListCount} Ops Users
                  </CustomTypography>
                </Box>
              </Grid2>
              {OpsStaffListCount > rowsPerPage && (
                <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                  <Box display="flex" justifyContent="end">
                    <CustomPagination
                      id="ops-staff-list-pagination"
                      count={Math.ceil(OpsStaffListCount / rowsPerPage)}
                      page={page + 1}
                      onPageChange={handlePageChange}
                      color="standard"
                    />
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Box>
      <Box>
        <Box>
          <FetchEntraUsers
            openAzureEntraUsers={openAzureEntraUsersForOpsStaff}
            onAzureEntraClose={() => handleOpenAzureEntraForOpsStaffForm(false)}
            azureEntraUsers={azureEntraUsers ?? []}
            isLoadingAzureEntraUsers={isLoadingAzureEntraUsers}
            path={NEW_OPSSTAFF_ROUTE_PATH}
            title="Ops User"
          />
        </Box>
      </Box>
      <Box>
        <CustomPopover
          open={Boolean(anchorOpsStaffListEl)}
          anchorEl={anchorOpsStaffListEl}
          onClose={() => setanchorOpsStaffListEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24">
            <Box className="text-align-left">
              <CustomTypography
                id="intake-form-filter-title"
                variant="h4"
                color="textPrimary"
              >
                Filters
              </CustomTypography>
              <Box className="pt-8">
                <CustomTypography
                  id="intake-form-filter-subtitle"
                  variant="body2"
                  color="textDisabled"
                >
                  Filter records by role and status.
                </CustomTypography>
              </Box>
            </Box>
            <Box className="pt-16">
              <CustomDivider />
            </Box>
            <Box className="flex flex-column">
              <Grid2
                container
                justifyContent="center"
                spacing={1}
                className="width-100"
              >
                <Grid2 size={{ xs: 12 }}>
                  <Box>
                    <Box className="flex-between-center pt-24">
                      <Box>
                        <CustomTypography
                          id="intake-form-role-mapping-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          ROLES
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8">
                      <RoleFilterSelector
                        data={PartnerFacilityFilterData}
                        value={filterRoleIds}
                        onChange={setFilterRoleIds}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box className="flex-between-center pt-24">
                      <Box>
                        <CustomTypography
                          id="intake-form-role-mapping-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          STATUS
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8">
                      <StatusFilterSelector
                        data={PartnerFacilityFilterData}
                        value={filterStatusId}
                        isForConsult={false}
                        onChange={setFilterStatusId}
                      />
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pt-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-facility-user-button"
                        onClick={() => {
                          setanchorOpsStaffListEl(null);
                          setFilterStatusId([]);
                          setSelectedStatusId([]);
                          setFilterRoleIds([]);
                          setSelectedRoleIds([]);
                          setFilterContext({
                            ...filterContext,
                            statusIds: [],
                            roleIds: [],
                          });
                        }}
                        variant="outlined"
                      >
                        Clear
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="apply-facility-user-filter-button"
                        onClick={() => {
                          setanchorOpsStaffListEl(null);
                          handleApplyFilters();
                        }}
                        variant="contained"
                      >
                        Apply
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default OpsStaffList;
